import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './ProfileMenu.css';
import ConnectionsIcon from '../Assests/Connections.svg';
import ProfileIcon from '../Assests/profile.svg';
import inviteFriends from "../Assests/invite_friends.png"
import MessageIcon from '../Assests/messageLight.svg';
import SettingsIcon from '../Assests/settings.svg';
import LogoutIcon from '../Assests/logout.svg';
import { setIsChatOpen, clearUserMessage, setValue, toggleSearchPageLoad } from '../redux/valueSlice';
import { setProfileShow } from '../redux/notificationSlice';
import PropTypes from 'prop-types';
import { useAuth } from '../Routes/AuthContext';
import { useSubscription } from '../Routes/SubscriptionContext';
import Switch from "../Components/switch"
import { information, getUserProfileDetails } from '../Services/ShowsApi';
import { API_STATUS } from '../utils/constant';
import { setData } from '../redux/userSlice';

const ProfileMenu = ({ isOpen, onClose, connectionCount = 20, UserID, Collaborate, onOpenSocialInvite }) => {
    const [isOpenToCollaborate, setIsOpenToCollaborate] = useState(Boolean(Collaborate))
    const [isUpdating, setIsUpdating] = useState(false)
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { logout } = useAuth();
    const { hasAccess } = useSubscription();
    
    useEffect(() => {
        setIsOpenToCollaborate(Boolean(Collaborate));
    }, [Collaborate]);

    const handleMessageClick = () => {
        dispatch(clearUserMessage());
        dispatch(setIsChatOpen(true));
        onClose(); // Close the profile menu
    };

    const handleConnectionsClick = () => {
        navigate('/community', { state: { viewAll: true } });
        onClose(); // Close the profile menu
    };

    const handleViewProfile = () => {
        dispatch(setValue({ viewSearchProfile: false })); // Show own profile
        dispatch(setProfileShow(true));
        onClose(); // Close the profile menu
    };

    const handleSettingsClick = () => {
        navigate('/settings/subscription');
        onClose();
    };

    const handleLogoutClick = () => {
        localStorage.clear();
        logout();
        onClose();
    };

    const toggleSwitch = async () => {
        if (isUpdating) return;
        
        setIsUpdating(true);
        let newCollaborateValue;
        
        try {
            newCollaborateValue = !isOpenToCollaborate;
            setIsOpenToCollaborate(newCollaborateValue);
            
            await information({
                UserID: UserID,
                Collaborate: newCollaborateValue,
            });
            
            const { data } = await getUserProfileDetails("UserID");
            if (data?.body?.Data) {
                const updatedUserData = data.body.Data[0];
                localStorage.setItem('user_profile_data', JSON.stringify(updatedUserData));
                dispatch(setData(updatedUserData));
            }
            dispatch(toggleSearchPageLoad());
        } catch (error) {
            console.error('Error updating collaboration status:', error);
            setIsOpenToCollaborate(!newCollaborateValue);
        } finally {
            setIsUpdating(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="profile-menu-overlay"
            onClick={e => {
                if (e.target === e.currentTarget) onClose();
            }}
            onKeyDown={e => {
                if (e.key === 'Escape') onClose();
            }}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Profile menu"
        >
            <div
                className="profile-menu-container"
                style={hasAccess ? undefined : { minWidth: '10vw' }}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => {
                    if (e.key === 'Escape') onClose();
                }}
                role="menu"
                tabIndex={-1}
            >
                {hasAccess && (
                    <>
                        <button
                            type="button"
                            className="profile-menu-item connections-item"
                            onClick={handleConnectionsClick}
                        >
                            <span>{connectionCount} Connections</span>
                            <img src={ConnectionsIcon} alt="Connections" className="profile-menu-icon" />
                        </button>

                        <div className='profile_userCollab_status'>
                            <span>
                                {isOpenToCollaborate ? "Open to Collaboration" : "Closed to Collaboration"}
                            </span>
                            <div className='profile_userCollab_button' title={isUpdating ? "Updating..." : ""}>
                                <Switch isOn={isOpenToCollaborate} toggleSwitch={toggleSwitch} disabled={isUpdating} />
                            </div>
                        </div>
                        <hr className="profile-menu-section-divider" />
                        <button
                            type="button"
                            className="profile-menu-item"
                            onClick={handleViewProfile}
                        >
                            <span>View Profile</span>
                            <img src={ProfileIcon} alt="View Profile" className="profile-menu-icon" />
                        </button>
                        <button
                            type="button"
                            className="profile-menu-item"
                            onClick={handleMessageClick}
                        >
                            <span>Message</span>
                            <img src={MessageIcon} alt="Message" className="profile-menu-icon" />
                        </button>
                        <button
                            type="button"
                            className="profile-menu-item"
                            onClick={() => {
                                if (onOpenSocialInvite) {
                                    onOpenSocialInvite();
                                }
                                onClose();
                            }}
                        >
                            <span>Invite friends</span>
                            <img src={inviteFriends} alt="Message" className="profile-menu-icon invite-friends-icon" />
                        </button>
                        <button
                            type="button"
                            className="profile-menu-item"
                            onClick={handleSettingsClick}
                        >
                            <span>Settings</span>
                            <img src={SettingsIcon} alt="Settings" className="profile-menu-icon" />
                        </button>
                        <hr className="profile-menu-section-divider" />
                    </>
                )}
                <button
                    type="button"
                    className="profile-menu-item"
                    onClick={handleLogoutClick}
                >
                    <span>Logout</span>
                    <img src={LogoutIcon} alt="Logout" className="profile-menu-icon" />
                </button>
            </div>
        </div>,
        document.body
    );
};

ProfileMenu.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    connectionCount: PropTypes.number,
    UserID: PropTypes.string.isRequired,
    Collaborate: PropTypes.bool.isRequired,
    onOpenSocialInvite: PropTypes.func
};

export default ProfileMenu; 